/**
 * Manual PO attribution — telling the agent which ServiceTitan purchase order
 * an invoice or receipt belongs to, when it reconciled against the wrong one,
 * proposed a new PO for a document that already has one, or never found it.
 * A button owning the dialog and the mutation, like `manual-job-match.tsx`.
 *
 * The PO is authoritative: one on another job moves the document there. The
 * server marks the choice manual, the agent keeps to it, and a change rejects
 * the agent's open proposals and queues a fresh run.
 */
import { useState } from "react";
import { Button } from "@/core/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandList,
} from "@/core/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import {
  useMatchDocumentPurchaseOrder,
  useSearchDocumentPurchaseOrders,
} from "@/accounts-payable/query";
import type {
  ApDocumentType,
  ApPurchaseOrderOption,
} from "@/accounts-payable/types";
import {
  documentTypeLabel,
  purchaseOrderLabel,
  purchaseOrderSearchStatusMessage,
} from "@/accounts-payable/utils";
import { getErrorMessage } from "@/core/api/errors";
import { useDebounce } from "@/core/hooks";
import { PurchaseOrderOptionItem } from "./purchase-order-option";
import { TextButton } from "./shared";

/** Each distinct query is a live ServiceTitan lookup, so wait for a pause. */
const SEARCH_DEBOUNCE_MS = 400;

/**
 * The trigger's look: a button (sized and coloured like any other), or a link
 * beside a review row's details, which takes neither.
 */
type PurchaseOrderMatchTrigger =
  | {
      appearance?: "button";
      size?: "xs" | "xsPill" | "sm";
      variant?: "default" | "secondary" | "tertiary" | "text";
    }
  | { appearance: "link"; size?: never; variant?: never };

export const PurchaseOrderMatchButton = ({
  businessId,
  documentId,
  documentType,
  agentRunId,
  hasJob,
  label = "Match to a PO",
  ariaLabel,
  className,
  ...trigger
}: {
  businessId: string | null;
  documentId: string;
  documentType: ApDocumentType;
  /** The document's newest run id, to tell whether the pick queued a new one. */
  agentRunId: string | null;
  /** Whether the document has a job, whose POs the dialog opens on. */
  hasJob: boolean;
  label?: string;
  /** For a list that repeats the label on every row: which one this is. */
  ariaLabel?: string;
  className?: string;
} & PurchaseOrderMatchTrigger) => {
  const [open, setOpen] = useState(false);
  // Here rather than in the form: the form unmounts with the dialog, and
  // React Query only runs mutate-level callbacks while the observer lives.
  const matchPo = useMatchDocumentPurchaseOrder(businessId, documentType);
  const kind = documentTypeLabel(documentType);

  return (
    <>
      {trigger.appearance === "link" ? (
        <TextButton
          className={className}
          aria-label={ariaLabel}
          onClick={() => setOpen(true)}
        >
          {label}
        </TextButton>
      ) : (
        <Button
          size={trigger.size ?? "xsPill"}
          variant={trigger.variant ?? "secondary"}
          className={className}
          aria-label={ariaLabel}
          onClick={() => setOpen(true)}
        >
          {label}
        </Button>
      )}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          // A save in flight holds the dialog open, so its outcome has
          // somewhere to land.
          if (matchPo.isPending) return;
          setOpen(next);
        }}
      >
        <DialogContent
          className="max-w-[560px] gap-6"
          description={`Choose the ServiceTitan purchase order this ${kind.lower} belongs to`}
        >
          <DialogHeader className="pb-0 text-left">
            <DialogTitle>Match {kind.lower} to a purchase order</DialogTitle>
          </DialogHeader>
          {open && (
            <PurchaseOrderMatchForm
              businessId={businessId}
              documentId={documentId}
              documentType={documentType}
              agentRunId={agentRunId}
              hasJob={hasJob}
              matchPo={matchPo}
              onClose={() => setOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const PurchaseOrderMatchForm = ({
  businessId,
  documentId,
  documentType,
  agentRunId,
  hasJob,
  matchPo,
  onClose,
}: {
  businessId: string | null;
  documentId: string;
  documentType: ApDocumentType;
  agentRunId: string | null;
  hasJob: boolean;
  matchPo: ReturnType<typeof useMatchDocumentPurchaseOrder>;
  onClose: () => void;
}) => {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<ApPurchaseOrderOption | null>(null);
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const {
    data: options = [],
    isLoading,
    isError,
    isPlaceholderData,
  } = useSearchDocumentPurchaseOrders(
    businessId,
    documentType,
    documentId,
    debouncedQuery,
  );
  const kind = documentTypeLabel(documentType).lower;

  // A selection belongs to the results it was picked from (see the job match).
  const onQueryChange = (value: string) => {
    setQuery(value);
    if (value.trim() !== query.trim()) setSelected(null);
  };

  const statusMessage = purchaseOrderSearchStatusMessage({
    query,
    hasJob,
    isLoading: isLoading || isPlaceholderData || query !== debouncedQuery,
    isError,
    resultCount: options.length,
  });

  const save = (option: ApPurchaseOrderOption | null) =>
    matchPo.mutate(
      { documentId, stPurchaseOrderId: option?.st_purchase_order_id ?? null },
      {
        onSuccess: (document) => {
          const outcome = option
            ? `${documentTypeLabel(documentType).capitalized} matched to ${purchaseOrderLabel(option)}.`
            : `${documentTypeLabel(documentType).capitalized} marked as having no existing PO.`;
          // The server rejects the agent's open proposals and queues a run
          // that reads the pick, after a short wait so several edits share it.
          const rerunQueued =
            document.agent_run_id !== null &&
            document.agent_run_id !== agentRunId;
          // "Against it" only reads right when there is a PO to check against.
          const followUp = option
            ? `The agent will re-check this ${kind} against it in a couple of minutes.`
            : `The agent will re-check this ${kind} in a couple of minutes.`;
          toast.success(rerunQueued ? `${outcome} ${followUp}` : outcome);
          onClose();
        },
        onError: (error: unknown) =>
          toast.error("Couldn’t change the purchase order", {
            description: getErrorMessage(error),
          }),
      },
    );

  return (
    <>
      <Stack gap="md" fullWidth className="px-6">
        <Typography variant="bodyW2" textColor="secondary">
          The agent will reconcile this {kind} against the PO you choose and
          won’t file another. A PO on a different job moves the {kind} to that
          job.
        </Typography>
        <Command
          shouldFilter={false}
          label="Search purchase orders"
          className="overflow-visible rounded-lg border border-stroke-tertiary"
        >
          <CommandInput
            value={query}
            onValueChange={onQueryChange}
            disabled={matchPo.isPending}
            // The dialog opens on the job's POs; typing is the other way in.
            autoFocus
            placeholder="PO number or job number"
          />
          <CommandList className="max-h-72 overflow-y-auto">
            {statusMessage ? (
              <Stack
                align="center"
                fullWidth
                role="status"
                className="px-4 py-6 text-center text-md text-tertiary-text"
              >
                {statusMessage}
              </Stack>
            ) : (
              <CommandGroup
                heading={
                  query.trim() || !hasJob ? undefined : "POs on this job"
                }
              >
                {options.map((option) => (
                  <PurchaseOrderOptionItem
                    key={option.st_purchase_order_id}
                    option={option}
                    selected={
                      selected?.st_purchase_order_id ===
                      option.st_purchase_order_id
                    }
                    disabled={matchPo.isPending}
                    onSelect={() => setSelected(option)}
                  />
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </Stack>
      {/* One row at every width, like the pricebook dialog's footer. */}
      <DialogFooter className="flex-row flex-wrap items-center justify-between gap-3 sm:justify-between sm:space-x-0">
        {/* For a document whose cost isn't filed anywhere yet: the agent then
            files a new PO rather than reconciling against one it found. */}
        <TextButton
          tone="secondary"
          onClick={() => save(null)}
          disabled={matchPo.isPending}
          className="whitespace-nowrap"
        >
          No existing PO
        </TextButton>
        <Stack direction="row" gap="sm">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={matchPo.isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => selected && save(selected)}
            loading={matchPo.isPending}
            disabled={!selected || matchPo.isPending}
          >
            Use this PO
          </Button>
        </Stack>
      </DialogFooter>
    </>
  );
};
